import cn from 'classnames';
import { useContext, forwardRef } from 'react';

import Check from 'components/icons/accent/CheckCircle';
import XCircle from 'components/icons/accent/XCircle';
import { BrandContext } from 'context/BrandContext';
import styles from 'styles/components/common/ToggleButton';

const IconMap = {
  preference: Check,
  avoidancePref: null,
  avoidance: XCircle,
  box: null,
  boxRecommender: null,
  plan: null,
  menuFilter: null,
};

const ToggleButton = forwardRef(
  (
    {
      className = '',
      disabled = false,
      textContainerClassName = '',
      children,
      handleClick,
      icon: Icon,
      id,
      name,
      role,
      selected,
      type,
      renderIconMultipleTimes,
      refs,
      setSelected,
      ...restProps
    },
    ref
  ) => {
    const { brand } = useContext(BrandContext);
    const IconComponent = type === 'preference' ? null : IconMap[type];
    const handleKeyDown = event => {
      if (!refs || !setSelected) return;

      const currentIndex = refs.findIndex(r => r.current === event.currentTarget);
      if (currentIndex === -1) return;

      let newIndex = null;
      const length = refs.length;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        newIndex = (currentIndex + 1) % length;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        newIndex = (currentIndex - 1 + length) % length;
      }

      if (newIndex !== null) {
        event.preventDefault();
        setSelected(Number(refs[newIndex].current.id));
        refs[newIndex].current.focus();
      }
    };

    return (
      <button
        id={id}
        name={name}
        disabled={disabled}
        role={role}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={cn(className, 'textMd', styles[brand], styles[type], {
          [styles.selected]: selected,
          [styles.unselected]: !selected,
        })}
        aria-checked={role === 'checkbox' || role === 'radio' ? selected : undefined}
        aria-pressed={role === 'button' ? selected : undefined}
        aria-selected={role === 'option' ? selected : undefined}
        onMouseUp={event => event.currentTarget.blur()}
        ref={ref}
        {...restProps}
      >
        {Icon ? (
          <div className={textContainerClassName}>
            {children}
            <div className="ml-auto flex space-x-1">
              {renderIconMultipleTimes ? (
                Array.from({ length: Number(renderIconMultipleTimes) }).map((_, i) => (
                  <Icon key={i} />
                ))
              ) : (
                <Icon />
              )}
            </div>
          </div>
        ) : (
          children
        )}
        {IconComponent && (
          <IconComponent
            className={cn('h-6 w-6 rounded-full bg-white-100', {
              [styles.topRight]: selected,
              hidden: !selected,
            })}
          />
        )}
      </button>
    );
  }
);

ToggleButton.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  name: PropTypes.string,
  selected: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func,
  type: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  role: PropTypes.string,
  children: PropTypes.node.isRequired,
  icon: PropTypes.elementType,
  textContainerClassName: PropTypes.string,
  renderIconMultipleTimes: PropTypes.number,
  refs: PropTypes.arrayOf(PropTypes.shape({ current: PropTypes.instanceOf(Element) })),
  setSelected: PropTypes.func,
};

export default ToggleButton;
